import { Controller } from '@hotwired/stimulus'

// Instant inbox search (#299). Scoped on the toolbar wrapper so ONE controller
// owns the search form, the filter chips, and the sibling filter toolbar.
//
// - search(): a 200ms debounced requestSubmit that navigates the
//   "conversation_results" Turbo Frame (mirrors segment_preview's debounce). No
//   request-sequence guard — Turbo coalesces overlapping frame navigations.
// - toggleToken(): field-aware, idempotent chip toggling directly on the query
//   field's text (present -> remove; a same-field is:/in: token -> replace;
//   else append), then re-search.
// - navigateWithQuery(): a single delegated click handler on the out-of-frame
//   filter toolbar that merges the LIVE typed q into the clicked axis link before
//   navigating (the server-rendered q on those links is stale once the user types).
// - updateCount(): after each frame swap, mirror the frame's data-conversation-count
//   into a visually-hidden status node so screen readers hear the new result count.
export default class extends Controller {
  static targets = ['form', 'query', 'status']

  connect() {
    // turbo:frame-render fires on the results frame and bubbles to document, but the
    // frame is a SIBLING of this controller's element (not a descendant), so a
    // data-action can't catch it — listen at the document level and filter by frame id.
    this.onFrameRender = this.updateCount.bind(this)
    document.addEventListener('turbo:frame-render', this.onFrameRender)
  }

  disconnect() {
    clearTimeout(this.debounce)
    document.removeEventListener('turbo:frame-render', this.onFrameRender)
  }

  // Debounced submit — fires 200ms after the last keystroke.
  search() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.formTarget.requestSubmit(), 200)
  }

  // Toggle one operator token in the query field, then re-search. Idempotent and
  // field-aware: a token already present is removed; otherwise any other token in the
  // SAME mutually-exclusive field (the prefix before ':', e.g. is:/in:) is dropped
  // before the new token is appended. Free-text residual (e.g. "refund") is preserved
  // because it carries no ':' and so never shares a field.
  toggleToken(event) {
    const token = event.currentTarget.dataset.token
    const tokens = this.queryTarget.value.split(/\s+/).filter(Boolean)
    let next

    if (tokens.includes(token)) {
      next = tokens.filter((existing) => existing !== token)
    } else {
      const field = this.fieldOf(token)
      const kept = field ? tokens.filter((existing) => this.fieldOf(existing) !== field) : tokens
      next = [...kept, token]
    }

    this.queryTarget.value = next.join(' ')
    this.syncPressed(next)
    this.search()
  }

  // Merge the live typed q into an out-of-frame filter link at click time, encoding-safe
  // via URLSearchParams, then navigate. Delegated on the toolbar; only its axis <a>
  // links are handled (chip buttons and dropdown toggles are not links).
  navigateWithQuery(event) {
    const link = event.target.closest('a[href]')
    if (!link) return

    const url = new URL(link.href, window.location.origin)
    const q = this.queryTarget.value.trim()
    if (q) url.searchParams.set('q', q)
    else url.searchParams.delete('q')

    event.preventDefault()
    Turbo.visit(url.toString())
  }

  // Reflect the current token set onto every chip's pressed state — so a replaced
  // token (is:open -> is:closed) un-presses the old chip, not just presses the new one.
  syncPressed(tokens) {
    this.element.querySelectorAll('[data-token]').forEach((button) => {
      const pressed = tokens.includes(button.dataset.token)
      button.setAttribute('aria-pressed', pressed ? 'true' : 'false')
      button.classList.toggle('btn-primary', pressed)
      button.classList.toggle('btn-outline-secondary', !pressed)
    })
  }

  // Announce the swapped frame's result count. Only the results frame carries the
  // data-conversation-count node, so other frame renders on the page are ignored.
  updateCount(event) {
    if (!event.target || event.target.id !== 'conversation_results') return
    if (!this.hasStatusTarget) return

    const node = event.target.querySelector('[data-conversation-count]')
    const count = node ? Number(node.dataset.conversationCount) || 0 : 0
    // Pluralize by count so "1 conversation" reads correctly (not "1 conversations").
    this.statusTarget.textContent = `${count} ${count === 1 ? 'conversation' : 'conversations'}`
  }

  // The mutually-exclusive field of an operator token — the prefix before ':'.
  // Free text (no ':') has no field, so it never collides on toggle/replace.
  fieldOf(token) {
    return token.includes(':') ? token.split(':')[0] : null
  }
}
